import type {
  AppealStatus,
  AttributionStatus,
  CashoutRequest,
  Currency,
  FraudStatus,
  Market,
  SuspensionReasonCode,
  Transaction,
  TransactionStatus,
  User,
} from '../dataProvider/types';

/**
 * The Fraud queue query/aggregation module - the ONE place flagged users are
 * ranked and their evidence assembled. Same seam idiom as subscriptions /
 * referrals / attribution: v1 aggregates client-side from the mock provider, every
 * selector lives here, and the late real-data migration swaps this module for a
 * server-side endpoint without the surface changing.
 *
 * THE MODEL (D-fraud-queue). The queue ranks USERS by riskScore (descending), the
 * lead Phase 5 surface. riskScore is engine-written stored mock data; the admin
 * displays and ACTS on it, never computes it. Transactions and payouts are NOT
 * independently ranked (no per-entity risk signal exists, and inventing one is out
 * of scope): they appear as EVIDENCE on a flagged user, the stress markers that
 * genuinely exist (high-value orders, SLA-breached payouts, manually-attributed
 * formerly-orphan transactions), shown as FACTS not as invented scores.
 *
 * The queue is INVESTIGATIVE-GLOBAL: a fraud ring spans markets, so by default it
 * shows flagged users across both markets, NOT switcher-scoped. An optional region
 * facet filters the user's market by exact value (the audit-log pattern), never via
 * marketsForScope.
 */

const DAY = 86_400_000;

/** Users at or above this score are in the actionable queue. [REVIEW: RISK_THRESHOLD] */
export const FRAUD_RISK_THRESHOLD = 50;

/** The length of a suspension, in days, from the moment it is applied. [REVIEW] */
export const SUSPENSION_DAYS = 30;

/** How many evidence rows of each kind to surface (the worst first). */
const EVIDENCE_LIMIT = 5;

export interface TxnEvidence {
  id: number;
  grossAmount: number;
  currency: Currency;
  status: TransactionStatus;
  occurredAt: string;
}

export interface PayoutEvidence {
  id: number;
  amount: number;
  currency: Currency;
  status: string;
  slaBreachedAt: string;
}

export interface OrphanEvidence {
  id: number;
  grossAmount: number;
  currency: Currency;
  attributionStatus: AttributionStatus;
}

export interface FraudEvidence {
  /** The user's largest orders, by gross amount (a high-value stress marker). */
  highValueTxns: TxnEvidence[];
  /** The user's SLA-breached cashout requests (a stressed-payout marker). */
  breachedPayouts: PayoutEvidence[];
  /** The user's manually-attributed (formerly orphan) transactions. */
  orphanAttributions: OrphanEvidence[];
}

export interface FraudRow {
  id: number;
  name: string;
  email: string;
  market: Market;
  riskScore: number;
  fraudStatus: FraudStatus;
  suspensionEndsAt: string | null;
  fraudReason: string | null;
  escalated: boolean;
  /** The current hold's structured category, carried so a reinstate can diff it to
   *  null in the audit trail (D-suspension-flow). */
  suspensionReasonCode: SuspensionReasonCode | null;
  /** The current appeal state, carried so a reinstate can resolve a pending appeal
   *  (D-suspension-flow). */
  appealStatus: AppealStatus;
  evidence: FraudEvidence;
}

export interface FraudQueueView {
  rows: FraudRow[];
}

export interface FraudQueueInput {
  users: User[];
  transactions: Transaction[];
  cashoutRequests: CashoutRequest[];
}

export interface FraudQueueOptions {
  /** Exact-value market facet (uk / us), or null for the global view. */
  regionFilter?: Market | null;
}

/** A user is in the actionable queue when they are risky enough OR already carry a
 *  recorded fraud decision (so a suspended or banned user stays visible to act on,
 *  e.g. to reinstate). A low-risk, active, un-escalated user is excluded. */
const isActionable = (u: User): boolean =>
  u.riskScore >= FRAUD_RISK_THRESHOLD ||
  u.fraudStatus !== 'active' ||
  u.escalated;

export function buildFraudQueueView(
  input: FraudQueueInput,
  opts: FraudQueueOptions = {},
): FraudQueueView {
  const txnsByUser = new Map<number, Transaction[]>();
  for (const t of input.transactions) {
    if (t.userId == null) continue; // orphans belong to no user yet
    const arr = txnsByUser.get(t.userId) ?? [];
    arr.push(t);
    txnsByUser.set(t.userId, arr);
  }
  const payoutsByUser = new Map<number, CashoutRequest[]>();
  for (const c of input.cashoutRequests) {
    const arr = payoutsByUser.get(c.userId) ?? [];
    arr.push(c);
    payoutsByUser.set(c.userId, arr);
  }

  const evidenceFor = (userId: number): FraudEvidence => {
    const txns = txnsByUser.get(userId) ?? [];
    const payouts = payoutsByUser.get(userId) ?? [];
    return {
      highValueTxns: [...txns]
        .sort((a, b) => b.grossAmount - a.grossAmount)
        .slice(0, EVIDENCE_LIMIT)
        .map((t) => ({
          id: t.id,
          grossAmount: t.grossAmount,
          currency: t.currency,
          status: t.status,
          occurredAt: t.occurredAt,
        })),
      breachedPayouts: payouts
        .filter((c) => c.slaBreachedAt != null)
        .slice(0, EVIDENCE_LIMIT)
        .map((c) => ({
          id: c.id,
          amount: c.amount,
          currency: c.currency,
          status: c.status,
          slaBreachedAt: c.slaBreachedAt as string,
        })),
      orphanAttributions: txns
        .filter((t) => t.matchedClickId != null || t.attributionNote != null)
        .slice(0, EVIDENCE_LIMIT)
        .map((t) => ({
          id: t.id,
          grossAmount: t.grossAmount,
          currency: t.currency,
          attributionStatus: (t.attributionStatus ??
            'attributed') as AttributionStatus,
        })),
    };
  };

  const rows: FraudRow[] = input.users
    .filter(isActionable)
    .filter((u) => opts.regionFilter == null || u.market === opts.regionFilter)
    .sort((a, b) => b.riskScore - a.riskScore || a.name.localeCompare(b.name))
    .map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      market: u.market,
      riskScore: u.riskScore,
      fraudStatus: u.fraudStatus,
      suspensionEndsAt: u.suspensionEndsAt,
      fraudReason: u.fraudReason,
      escalated: u.escalated,
      suspensionReasonCode: u.suspensionReasonCode,
      appealStatus: u.appealStatus,
      evidence: evidenceFor(u.id),
    }));

  return { rows };
}

/** The ISO instant a suspension applied at `now` should end. */
export const suspensionEnd = (now: number): string =>
  new Date(now + SUSPENSION_DAYS * DAY).toISOString();
